/**
 * Bali (Asia/Makassar, UTC+8, no DST) week/month/quarter bucketing helpers for
 * the report charts.
 *
 * Extracted verbatim from `CheckInsReports.tsx`, where the same
 * week-start / month-key derivation and the matching short/full label
 * formatters were duplicated across the weekly and monthly chart queries.
 *
 * Bucketing is anchored to Bali local time — the gym's timezone — so each
 * Mon–Sun week and each calendar month bar matches the real business period,
 * independent of the admin's browser timezone.
 *
 * The date-key formatters reuse `getBaliDateKey` from `date-utils` because that
 * helper is provably the same `Intl.DateTimeFormat('en-CA', …)` configuration.
 * The label formatters are kept here verbatim: they intentionally render in
 * en-US, UTC-anchored, with a NON-padded day (e.g. "Apr 5", not "Apr 05"), so
 * they are NOT interchangeable with `formatBaliDayLabel`.
 */

import { getBaliDateKey } from '@/lib';

// Short x-axis label, e.g. "Apr 27" — UTC-anchored, non-padded day.
const weekLabelFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  day: 'numeric',
});

const monthLabelFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'short',
  year: 'numeric',
});

const monthFullLabelFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  month: 'long',
  year: 'numeric',
});

/** YYYY-MM-DD of the Monday that starts the Mon–Sun week containing a
 *  YYYY-MM-DD date key. Pure calendar arithmetic on the key — use this when
 *  the day is already a Bali date key and there is no instant to convert. */
export function weekStartOfKey(dayKey: string): string {
  const [y, m, day] = dayKey.split('-').map(Number);
  const anchor = new Date(Date.UTC(y, m - 1, day));
  const back = (anchor.getUTCDay() + 6) % 7; // days back to reach Monday
  return new Date(anchor.getTime() - back * 86_400_000)
    .toISOString()
    .slice(0, 10);
}

/** YYYY-MM-DD of the Monday that starts the Bali week containing `d`. */
export function baliWeekStart(d: Date): string {
  return weekStartOfKey(getBaliDateKey(d));
}

/** "Apr 27" for a YYYY-MM-DD date string. */
export function weekLabel(date: string): string {
  const [y, m, day] = date.split('-').map(Number);
  // Anchor at noon UTC so the calendar day is formatted as-is.
  return weekLabelFormatter.format(new Date(Date.UTC(y, m - 1, day, 12)));
}

/**
 * "Apr 5" for a YYYY-MM-DD date key — the same non-padded formatter as
 * `weekLabel`, named for daily axes so charts on one page label days and
 * weeks identically. (`formatBaliDayLabel` in date-utils pads the day.)
 */
export function dayLabel(date: string): string {
  return weekLabel(date);
}

/** YYYY-MM of the Bali calendar month containing `d`. */
export function baliMonthKey(d: Date): string {
  return getBaliDateKey(d).slice(0, 7);
}

/**
 * YYYY-MM-01 of the first day of the Bali calendar month containing `d`.
 * Month-start form of `baliMonthKey`, used where charts key buckets by a
 * full date string. `monthLabel` accepts either form.
 */
export function baliMonthStart(d: Date): string {
  return `${getBaliDateKey(d).slice(0, 7)}-01`;
}

/** "Apr 2026" for a YYYY-MM month key. */
export function monthLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  // Anchor at noon UTC on the 1st so the calendar month is formatted as-is.
  return monthLabelFormatter.format(new Date(Date.UTC(y, m - 1, 1, 12)));
}

/** Next "YYYY-MM" month key (handles December → January rollover). */
export function nextMonthKey(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  const total = y * 12 + (m - 1) + 1;
  return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/**
 * "YYYY-Qn" quarter key of the Bali calendar quarter containing a YYYY-MM or
 * YYYY-MM-DD date key (Q1 = Jan–Mar … Q4 = Oct–Dec).
 */
export function quarterKeyOf(dateKey: string): string {
  const [y, m] = dateKey.split('-').map(Number);
  return `${y}-Q${Math.floor((m - 1) / 3) + 1}`;
}

/** "Q3 2026" for a YYYY-Qn quarter key. */
export function quarterLabel(quarterKey: string): string {
  const [y, q] = quarterKey.split('-');
  return `${q} ${y}`;
}

/** Next "YYYY-Qn" quarter key (handles Q4 → next year's Q1 rollover). */
export function nextQuarterKey(quarterKey: string): string {
  const [y, q] = quarterKey.split('-');
  const n = Number(q.slice(1));
  return n === 4 ? `${Number(y) + 1}-Q1` : `${y}-Q${n + 1}`;
}

/** "April 2026" for a YYYY-MM month key. */
export function monthFullLabel(monthKey: string): string {
  const [y, m] = monthKey.split('-').map(Number);
  return monthFullLabelFormatter.format(new Date(Date.UTC(y, m - 1, 1, 12)));
}

/**
 * The last `count` Bali month keys (YYYY-MM), newest first, ending with the
 * month that is current *in Bali* — so an admin in London at 23:00 on Jul 31
 * still sees July at the top, not August.
 */
export function recentMonthKeys(count: number, now: Date = new Date()): string[] {
  const [y, m] = baliMonthKey(now).split('-').map(Number);
  return Array.from({ length: count }, (_, i) => {
    // Date.UTC normalizes a negative/overflowing month index across the year.
    const d = new Date(Date.UTC(y, m - 1 - i, 1, 12));
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
  });
}

/**
 * Inclusive Bali day bounds of a YYYY-MM month key:
 * `monthRange('2026-07')` → `{ start: '2026-07-01', end: '2026-07-31' }`.
 *
 * Both ends are plain Bali date keys, matching what the report hooks take. The
 * last day comes from day 0 of the *next* month, which Date.UTC resolves to the
 * final day of this one — leap-year correct for free (2028-02 → 29).
 */
export function monthRange(monthKey: string): { start: string; end: string } {
  const [y, m] = monthKey.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return {
    start: `${monthKey}-01`,
    end: `${monthKey}-${String(lastDay).padStart(2, '0')}`,
  };
}
